import React, { memo } from 'react';
import { shallowEqual } from 'react-redux';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import {
  NavBackBtn,
  NavHomeBtn,
  NavCommentBtn,
  NavMenuBtn,
} from 'public/assets';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { RootState } from '@store/store';
import { setOrderBy, setToastMessage } from '@slices/commentSlice';
import { SORT_BY } from '@common/values';
import { touchCommentButton } from '@lib/eventManager';
import { Story_Play_Type } from '@/baseType';

interface IChatHeader {
  title: string;
  onPopUp: () => void;
}

const ChatHeader = ({ title, onPopUp }: IChatHeader) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { playChapterInfo } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );
  const { featureVersion } = useAppSelector((state: RootState) => state.user);

  const router = useRouter();

  const onBack = () => {
    router.replace({
      pathname: playChapterInfo
        ? `/story/${playChapterInfo.playChapter.storyId}`
        : '/',
    });
  };

  const onGoHome = () => {
    router.replace('/');
  };

  const onGoComment = () => {
    dispatch(setOrderBy(SORT_BY.best));
    dispatch(setToastMessage(t('common_toast_title_show_best')));

    playChapterInfo &&
      router.push({
        pathname: '/comment',
        query: {
          storyId: playChapterInfo.playChapter.storyId,
          chapterId: playChapterInfo.playChapter.chapterId,
          chapterIndex: playChapterInfo.playChapter.chapterIndex,
          from: 'chat',
          playType: Story_Play_Type.Interactive,
        },
      });

    playChapterInfo &&
      touchCommentButton({
        storyName: playChapterInfo.playChapter.story.name,
        chapterIndex: playChapterInfo.playChapter.chapterIndex,
        from: 'chat',
        contentType: 'interactive',
      });
  };

  return (
    <nav css={container}>
      <div className="left">
        <div className="button">
          <NavBackBtn
            stroke={theme.colors.gray800}
            width={24}
            height={24}
            onClick={onBack}
          />
        </div>
        <div className="button">
          <NavHomeBtn width={24} height={24} onClick={onGoHome} />
        </div>
        <div className="title-text">{title}</div>
      </div>
      {featureVersion === 1 && (
        <div className="nav">
          <div className="button comment" onClick={onGoComment}>
            <NavCommentBtn />
          </div>
          <div className="button" onClick={onPopUp}>
            <NavMenuBtn />
          </div>
        </div>
      )}
    </nav>
  );
};

const container = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 4em;
  background: ${theme.colors.white};
  width: 100%;
  max-width: 100%;
  padding: 0 13px 0 20px;
  z-index: 10;

  .left {
    display: flex;
    align-items: center;
    gap: 1em;
    max-width: 80%;
  }

  .button {
    flex-shrink: 0;
    cursor: pointer;
    background-color: transparent;
  }

  .title-text {
    font-size: 1.25em;
    font-weight: 700;
    word-break: nowrap;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    color: ${theme.colors.gray900};
  }

  .nav {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .home {
    cursor: pointer;
  }

  .comment {
    margin-right: 18px;
  }

  .disable {
    pointer-events: none;
    opacity: 0.5;
  }
`;

export default memo(ChatHeader);
